import { createColumnHelper } from "@tanstack/react-table";
import { Group, Text } from "@mantine/core";
import { IconCalendarClock } from "@tabler/icons-react";
import type { ColumnDef } from "../table";
import { Payment } from "../../utils/interfaces";
import { displayCurrencyAmount, displayISODate } from "../../utils/display";
import { getPaymentStatusBadge } from "../../utils/consts";
import { BucketStatusCell } from "./BucketStatusCell";

const columnHelper = createColumnHelper<Payment>();

function dueDateSortValue(dueDate: string): number {
  const ms = Date.parse(dueDate);
  return Number.isNaN(ms) ? 0 : ms;
}

export function customerInvoiceStatusForDisplay(paymentStatus: string): string {
  const normalized = paymentStatus.toLowerCase();
  if (normalized === "paid") return "Paid";
  if (normalized === "unpaid" || normalized === "late") return "Open";
  return paymentStatus;
}

export function createCustomerInvoicesColumns(options: {
  showStatus: boolean;
}): ColumnDef<Payment, unknown>[] {
  const statusColumn = columnHelper.accessor("payment_status", {
    id: "payment_status",
    header: "Status",
    enableSorting: true,
    meta: { align: "start" },
    cell: ({ row }) => {
      const label = customerInvoiceStatusForDisplay(row.original.payment_status);
      const badge = getPaymentStatusBadge(label);
      return (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${badge.className}`}
        >
          {badge.label}
        </span>
      );
    },
  });

  // Always keep collection columns; Status is additive when showing paid.
  const collectionColumns = [
    columnHelper.display({
      id: "follow_up",
      header: "Follow Up",
      enableSorting: false,
      meta: { align: "start" },
      cell: ({ row }) => {
        const followUp = row.original.next_follow_up;
        if (!followUp) {
          return (
            <Text size="sm" c="dimmed" className="italic">
              -
            </Text>
          );
        }
        return (
          <Group gap="xs">
            <IconCalendarClock size={16} className="text-blue-600" />
            <div>
              <Text size="sm" fw={500} className="text-gray-900">
                {new Date(followUp.follow_up_date).toLocaleDateString("en-IL", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Text>
              {followUp.description && (
                <Text size="xs" c="dimmed" className="mt-1">
                  {followUp.description}
                </Text>
              )}
            </div>
          </Group>
        );
      },
    }),
    columnHelper.display({
      id: "bucket_status",
      header: "Bucket Status",
      enableSorting: false,
      meta: { align: "start", cellClassName: "whitespace-nowrap" },
      cell: ({ row }) => (
        <BucketStatusCell payment={row.original} variant="status" />
      ),
    }),
    columnHelper.display({
      id: "bucket_author",
      header: "",
      enableSorting: false,
      meta: { align: "start", cellClassName: "whitespace-nowrap" },
      cell: ({ row }) => (
        <BucketStatusCell payment={row.original} variant="authorBadge" />
      ),
    }),
  ];

  return [
    columnHelper.accessor("external_id", {
      header: "Invoice ID",
      enableSorting: true,
      sortingFn: "alphanumeric",
      meta: { align: "start" },
      cell: ({ row }) => (
        <Text fw={600} size="sm" className="text-gray-900">
          {row.original.external_id}
        </Text>
      ),
    }),
    ...(options.showStatus ? [statusColumn] : []),
    columnHelper.accessor("amount", {
      header: "Amount",
      enableSorting: true,
      meta: { align: "start", cellClassName: "whitespace-nowrap" },
      cell: ({ row }) => (
        <Text fw={500} size="sm" className="text-gray-900">
          {displayCurrencyAmount(row.original.amount, row.original.currency_type)}
        </Text>
      ),
    }),
    columnHelper.accessor("due_date", {
      header: "Due Date",
      enableSorting: true,
      sortingFn: (rowA, rowB) =>
        dueDateSortValue(rowA.original.due_date) -
        dueDateSortValue(rowB.original.due_date),
      meta: { align: "start", cellClassName: "whitespace-nowrap" },
      cell: ({ row }) => (
        <Text size="sm" className="text-gray-700">
          {displayISODate(row.original.due_date)}
        </Text>
      ),
    }),
    ...collectionColumns,
  ];
}
